import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import type { Layout, Layouts } from 'react-grid-layout';
import { profileService } from '../services/profileService';
import { templateService } from '../services/templateService';
import { useAuth } from './AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  MAX_DASHBOARDS,
  type Dashboard,
  type DashboardState,
  type DashboardContextType,
  type DashboardTemplate,
  type ToolType,
  type ToolWindow,
} from '../types/dashboard';
import { isPopoutPath } from '../utils/popout';

// Default empty dashboard
const createEmptyDashboard = (id: number, name?: string): Dashboard => ({
  id,
  name: name || `Dashboard #${id}`,
  isDefault: false,
  layout: [],
  windows: [],
});

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

interface DashboardProviderProps {
  children: ReactNode;
}

const DEBOUNCE_MS = 2000; // 2 seconds — layout drag/resize only

// Widget types that have been removed from the platform.
// Filtered out on load so persisted dashboards don't break.
const REMOVED_WIDGET_TYPES = new Set([
  'steamroom-chat', 'zeroday-chat', 'wsj-only-chat',
  'uw-alerts-beta', 'source-compare', 'gex-surface', 'options-scanner',
  'cumulative-scanner', 'candle-chart-beta', 'credit-monitor', 'vol-surface',
  'flow-pulse', 'flow-scanner', 'wiseguy-qf-alerts', 'qf-curated',
]);

// Clamp layout item dimensions to their stored minH/minW constraints.
// Prevents widgets from loading below minimum size when a saved layout
// has a smaller-than-minimum height (e.g. after the user resized at a
// narrow breakpoint before minH enforcement was in place).
function enforceMinSize(layout: Layout[]): Layout[] {
  return layout.map((item) => ({
    ...item,
    h: Math.max(item.h, item.minH ?? 1),
    w: Math.max(item.w, item.minW ?? 1),
  }));
}

// Map subscription tier to default dashboard template slug.
// On first visit, new users get a pre-loaded dashboard based on their tier.
const TIER_DEFAULT_TEMPLATE: Record<string, string> = {
  steamroom: 'options-flow',
  zeroday: 'zeroday-default',
  wsjonly: 'wsj-only',
};
/**
 * Where a user with no recognised tier starts.
 *
 * ⚠️ Must name a template that EXISTS. When the slug does not resolve,
 * `loadTemplate` logs a console warning nobody reads and the user lands on an
 * empty dashboard with nothing saying why.
 *
 * Moved off 'pre-market' so that template can be RETIRED SERVER-SIDE without
 * stranding anyone: while the fallback pointed at it, deleting the row would
 * have emptied the dashboard of every user with no recognised tier. It is no
 * longer load-bearing, so the row can now simply go.
 */
const FALLBACK_TEMPLATE = 'options-flow';

function resolveOpenIds(
  dashboards: Dashboard[],
  savedOpenIds: number[] | undefined
): number[] {
  const known = new Set(dashboards.map((d) => d.id));
  if (savedOpenIds && savedOpenIds.length > 0) {
    const open = savedOpenIds.filter((id) => known.has(id));
    if (open.length > 0) return open;
  }
  // v1 payloads have no open list — every saved layout was a tab.
  return dashboards.map((d) => d.id);
}

function getHighestTier(stripeMetadata?: string[]): string | null {
  if (!stripeMetadata || stripeMetadata.length === 0) return null;
  const tierOrder = ['steamroom', 'zeroday', 'wsjonly'];
  for (const tier of tierOrder) {
    if (stripeMetadata.includes(tier)) return tier;
  }
  return null;
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { isToolVisible } = usePermissions();
  const hasUser = !!user;
  const [dashboards, setDashboards] = useState<Dashboard[]>([createEmptyDashboard(1, 'Dashboard #1')]);
  const [openDashboardIds, setOpenDashboardIds] = useState<number[]>([1]);
  const [currentDashboardId, setCurrentDashboardId] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [templates, setTemplates] = useState<DashboardTemplate[]>([]);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedStateRef = useRef<string>('');
  const rawFrontendStateRef = useRef<Record<string, unknown> | null>(null);
  const onboardingAppliedRef = useRef(false);
  // Create / rename / delete / switch should not wait for the layout debounce.
  const persistImmediatelyRef = useRef(false);
  // A pop-out window keeps its own settings. Its copy of the dashboards is only
  // what it loaded, so saving it would undo edits made in the dashboard tab.
  const [isPopout] = useState(() => isPopoutPath(window.location.pathname));

  const currentDashboard = dashboards.find((d) => d.id === currentDashboardId) || null;
  const openDashboards = openDashboardIds
    .map((id) => dashboards.find((d) => d.id === id))
    .filter((d): d is Dashboard => d !== undefined);
  const closedDashboards = dashboards.filter((d) => !openDashboardIds.includes(d.id));



  // Load dashboard state from backend on mount
  const loadFromBackend = useCallback(async () => {
    if (!isAuthenticated || !hasUser) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await profileService.getFrontendState();
      rawFrontendStateRef.current = response.frontend_state || null;
      const savedState = response.frontend_state?.dashboardState as DashboardState | undefined;

      if (savedState && savedState.dashboards && savedState.dashboards.length > 0) {
        // Strip removed widget types AND admin-only widgets the user can't see.
        // Non-adminOnly locked widgets stay in the layout — PermissionGate renders
        // an upgrade card in their slot instead of mounting the widget.
        const cleanedDashboards = savedState.dashboards.map((d) => {
          const removedIds = new Set(
            d.windows
              .filter((w) => REMOVED_WIDGET_TYPES.has(w.type) || !isToolVisible(w.type))
              .map((w) => w.id)
          );
          return {
            ...d,
            windows: d.windows.filter((w) => !removedIds.has(w.id)),
            layout: enforceMinSize(d.layout.filter((l) => !removedIds.has(l.i))),
            layouts: d.layouts
              ? (Object.fromEntries(
                  Object.entries(d.layouts).map(([bp, bpLayout]) => [
                    bp,
                    enforceMinSize((bpLayout as Layout[]).filter((l) => !removedIds.has(l.i))),
                  ])
                ) as Layouts)
              : undefined,
          };
        });

        const openIds = resolveOpenIds(cleanedDashboards, savedState.openDashboardIds);
        const savedCurrent = savedState.currentDashboardId;
        const currentId = openIds.includes(savedCurrent) ? savedCurrent : openIds[0];

        setDashboards(cleanedDashboards);
        setOpenDashboardIds(openIds);
        setCurrentDashboardId(currentId);
        lastSavedStateRef.current = JSON.stringify(savedState);
      } else {
        // No saved state, use default
        const defaultDashboard = createEmptyDashboard(1, 'Dashboard #1');
        defaultDashboard.isDefault = true;
        setDashboards([defaultDashboard]);
        setOpenDashboardIds([1]);
        setCurrentDashboardId(1);
      }
    } catch (error) {
      console.error('Failed to load dashboard state:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, hasUser, user?.id]);

  // Save dashboard state to backend (debounced)
  const saveToBackend = useCallback(async () => {
    if (!isAuthenticated || !hasUser || isPopout) return;

    const state: DashboardState = {
      dashboards,
      currentDashboardId,
      openDashboardIds,
      version: 2,
    };

    const stateJson = JSON.stringify(state);

    // Don't save if nothing changed
    if (stateJson === lastSavedStateRef.current) {
      return;
    }

    try {
      setIsSaving(true);
      await profileService.saveFrontendState({ dashboardState: state });
      lastSavedStateRef.current = stateJson;
      console.log('Dashboard state saved to backend');
    } catch (error) {
      console.error('Failed to save dashboard state:', error);
    } finally {
      setIsSaving(false);
    }
  }, [dashboards, currentDashboardId, openDashboardIds, isAuthenticated, hasUser, isPopout]);

  // Auto-save. Layout drag/resize stays debounced; structural edits
  // (create / rename / delete / switch) flip persistImmediatelyRef so the
  // next write is not sitting in a 2s timer when the user reloads.
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Leave the flag set until the write actually fires so React Strict Mode's
    // effect remount does not fall back to the 2s debounce and drop the save.
    const delay = persistImmediatelyRef.current ? 0 : DEBOUNCE_MS;

    saveTimeoutRef.current = setTimeout(() => {
      persistImmediatelyRef.current = false;
      saveToBackend();
    }, delay);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [dashboards, currentDashboardId, openDashboardIds, isAuthenticated, isLoading, saveToBackend]);

  // Flush a pending write if the tab hides or the page is leaving. Relies on
  // lastSavedStateRef so a no-op hide does not PATCH.
  useEffect(() => {
    const flush = (): void => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      void saveToBackend();
    };
    const onHide = (): void => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [saveToBackend]);

  // Fetch templates from API
  const fetchTemplates = useCallback(async () => {
    try {
      const fetchedTemplates = await templateService.getTemplates();
      setTemplates(fetchedTemplates);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  }, []);

  // Load state on mount
  useEffect(() => {
    loadFromBackend();
    fetchTemplates();
  }, [loadFromBackend, fetchTemplates]);

  // Auto-load default template for first-time users
  useEffect(() => {
    if (isLoading || !isAuthenticated || !hasUser || isPopout) return;
    if (templates.length === 0) return;
    if (onboardingAppliedRef.current) return;

    // Check if user already went through onboarding
    const frontendState = rawFrontendStateRef.current;
    if (frontendState?.onboardingCompleted) return;

    // Check if dashboard already has content (user has saved state)
    const dashboard = dashboards.find((d) => d.id === currentDashboardId);
    if (!dashboard || dashboard.windows.length > 0) return;

    // Determine template slug from subscription tier
    const highestTier = getHighestTier(user?.stripe_metadata);
    const templateSlug = highestTier
      ? TIER_DEFAULT_TEMPLATE[highestTier] || FALLBACK_TEMPLATE
      : FALLBACK_TEMPLATE;

    // Find and apply template
    const template = templates.find((t) => t.slug === templateSlug);
    if (!template) {
      console.warn('Default onboarding template not found:', templateSlug);
      return;
    }

    // Prevent re-triggering
    onboardingAppliedRef.current = true;

    // Apply template to current dashboard
    setDashboards((prev) =>
      prev.map((d) =>
        d.id === currentDashboardId
          ? { ...d, layout: [...template.layout], windows: [...template.windows] }
          : d
      )
    );

    // Save onboarding flag (non-blocking)
    profileService.saveFrontendState({ onboardingCompleted: true }).catch((error) => {
      console.error('Failed to save onboarding flag:', error);
    });

    console.log('Auto-loaded onboarding template:', templateSlug);
  }, [isLoading, isAuthenticated, hasUser, isPopout, templates, dashboards, currentDashboardId, user?.stripe_metadata]);

  // Dashboard management
  const switchDashboard = useCallback((id: number) => {
    persistImmediatelyRef.current = true;
    setOpenDashboardIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setCurrentDashboardId(id);
  }, []);

  const openDashboard = useCallback((id: number) => {
    persistImmediatelyRef.current = true;
    setOpenDashboardIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setCurrentDashboardId(id);
  }, []);

  const closeDashboard = useCallback((id: number) => {
    setOpenDashboardIds((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((openId) => openId !== id);
      persistImmediatelyRef.current = true;
      if (id === currentDashboardId) {
        setCurrentDashboardId(next[0]);
      }
      return next;
    });
  }, [currentDashboardId]);

  const createDashboard = useCallback((name?: string): number | null => {
    if (dashboards.length >= MAX_DASHBOARDS) return null;
    const newId = Math.max(...dashboards.map((d) => d.id), 0) + 1;
    persistImmediatelyRef.current = true;
    setDashboards((prev) => [...prev, createEmptyDashboard(newId, name)]);
    setOpenDashboardIds((prev) => [...prev, newId]);
    setCurrentDashboardId(newId);
    return newId;
  }, [dashboards]);

  const renameDashboard = useCallback((id: number, name: string) => {
    persistImmediatelyRef.current = true;
    setDashboards((prev) => prev.map((d) => (d.id === id ? { ...d, name } : d)));
  }, []);

  const setDefaultDashboard = useCallback((id: number) => {
    persistImmediatelyRef.current = true;
    setDashboards((prev) => prev.map((d) => ({ ...d, isDefault: d.id === id })));
  }, []);

  const deleteDashboard = useCallback((id: number) => {
    persistImmediatelyRef.current = true;
    let nextDashboards: Dashboard[] = [];
    setDashboards((prev) => {
      const filtered = prev.filter((d) => d.id !== id);
      nextDashboards = filtered.length === 0
        ? [createEmptyDashboard(1, 'Dashboard #1')]
        : filtered;
      return nextDashboards;
    });
    setOpenDashboardIds((prev) => {
      let next = prev.filter((openId) => openId !== id);
      if (next.length === 0) {
        next = [nextDashboards[0]?.id ?? 1];
      }
      if (!next.includes(currentDashboardId)) {
        setCurrentDashboardId(next[0]);
      }
      return next;
    });
  }, [currentDashboardId]);

  const resetDashboard = useCallback((id: number) => {
    setDashboards((prev) =>
      prev.map((d) => (d.id === id ? { ...d, layout: [], layouts: undefined, windows: [] } : d))
    );
  }, []);

  const loadTemplate = useCallback((templateSlug: string, dashboardId?: number) => {
    // Find template by slug
    const template = templates.find((t) => t.slug === templateSlug);
    if (!template) {
      console.error('Template not found:', templateSlug);
      return;
    }

    const targetId = dashboardId || currentDashboardId;
    setDashboards((prev) =>
      prev.map((d) =>
        d.id === targetId
          ? {
              ...d,
              layout: [...template.layout],
              layouts: undefined, // Let RGL regenerate per-breakpoint layouts
              windows: [...template.windows],
            }
          : d
      )
    );
  }, [currentDashboardId, templates]);

  // Window management
  const addWindow = useCallback((toolType: ToolType, config?: Record<string, unknown>) => {
    const dashboard = dashboards.find((d) => d.id === currentDashboardId);
    if (!dashboard) return;

    // Generate unique window ID
    const existingIds = dashboard.windows.map((w) => w.id);
    let counter = 1;
    let newId = `${toolType}-${counter}`;
    while (existingIds.includes(newId)) {
      counter++;
      newId = `${toolType}-${counter}`;
    }

    const newWindow: ToolWindow = {
      id: newId,
      type: toolType,
      config,
    };

    // Define size based on tool type
    //let defaultSize = { w: 6, h: 6, minW: 3, minH: 4 };
    let defaultSize: { w: number; h: number; minW?: number; maxW?: number; minH?: number; maxH?: number } = { w: 6, h: 6, minW: 3, minH: 4 };

    // Smaller, vertical size for compact widgets
    if (toolType === 'squeezo') {
      defaultSize = { w: 3, h: 3, minW: 1, minH: 2 };
    }

    // Wide table for Steamroom Levels
    if (toolType === 'steamroom-levels') {
      defaultSize = { w: 12, h: 8, minW: 3, minH: 5 };
    }

    // Wiseguy Alerts - needs space for gauges + filters + table
    if (toolType === 'wiseguy-alerts') {
      defaultSize = { w: 8, h: 12, minW: 2, minH: 6 };
    }

    // Upgrades/Downgrades - side-by-side tables
    if (toolType === 'upgrades-downgrades') {
      defaultSize = { w: 3, h: 12, minW: 3, minH: 6 };
    }

    // Congress Trades - table with search and pagination
    if (toolType === 'congress-trades') {
      defaultSize = { w: 3, h: 12, minW: 3, minH: 6 };
    }

    // Insider Trades - paginated table with transaction type filter
    if (toolType === 'insider-trades') {
      defaultSize = { w: 3, h: 12, minW: 3, minH: 6 };
    }

    // Market Movers - three-tab gainers/losers/active widget
    if (toolType === 'market-movers') {
      defaultSize = { w: 2, h: 12, minW: 2, minH: 6 };
    }

    // TD Sequential - chart widget
    if (toolType === 'td-sequential') {
      defaultSize = { w: 3, h: 12, minW: 3, minH: 8 };
    }

    // Niner Breadth - compact z-score gauge with metrics
    if (toolType === 'niner-breadth') {
      defaultSize = { w: 2, h: 12, minW: 2, minH: 12 };
    }

    // GEX Profile (Gamma Exposure) - side-by-side chart with candlesticks + GEX bars
    if (toolType === 'gex-profile') {
      defaultSize = { w: 3, h: 12, minW: 3, minH: 8 };
    }

    // GEX-Ray - intraday streaming chart with candlesticks + profile
    if (toolType === 'gex-ray') {
      defaultSize = { w: 3, h: 12, minW: 3, minH: 12 };
    }

    if (toolType === 'gex-heatmap') {
      defaultSize = { w: 8, h: 14, minW: 4, minH: 10 };
    }

    if (toolType === 'nq-scalper') {
      defaultSize = { w: 6, h: 12, minW: 4, minH: 8 };
    }

    // Zeroday Breadth - a row of four panes meant to sit under the Candle Chart
    if (toolType === 'zeroday-breadth') {
      defaultSize = { w: 6, h: 6, minW: 3, minH: 4 };
    }

    // Cumulative Premium - line chart with multiple series + OHLC candlesticks
    if (toolType === 'cumulative-premium') {
      defaultSize = { w: 3, h: 12, minW: 3, minH: 6 };
    }

    // FinancialJuice - Headlines (portrait/tall layout for news feed)
    if (toolType === 'fj-headlines') {
      defaultSize = { w: 2, h: 14, minW: 2, minH: 8 };
    }

    // FinancialJuice - Economic Calendar (portrait/tall layout)
    if (toolType === 'fj-economic-calendar') {
      defaultSize = { w: 3, h: 14, minW: 2, minH: 4 };
    }

    // FinancialJuice - Voice Squawk (narrow, compact)
    if (toolType === 'fj-voice') {
      defaultSize = { w: 3, h: 5, minW: 2, minH: 4 };
    }

    // FinancialJuice - Tickstrike (narrow, compact)
    if (toolType === 'fj-tickstrike') {
      defaultSize = { w: 3, h: 5, minW: 2, minH: 4 };
    }

    // Discord Composer (admin only - compact message form)
    if (toolType === 'discord-composer') {
      defaultSize = { w: 3, h: 8, minW: 3, minH: 6 };
    }

    // Sentiment Control (admin only - compact value setter)
    if (toolType === 'sentiment-control') {
      defaultSize = { w: 2, h: 5, minW: 2, minH: 4 };
    }

    // WSJ Feed - Wall St. Jesus feed widget
    if (toolType === 'wsj-feed') {
      defaultSize = { w: 3, h: 14, minW: 2, minH: 8 };
    }

    const newLayoutItem = {
      i: newId,
      x: 0,
      y: Infinity, // Add to bottom
      ...defaultSize,
    };

    // Add window to dashboard
    setDashboards((prev) =>
      prev.map((d) =>
        d.id === currentDashboardId
          ? {
              ...d,
              windows: [...d.windows, newWindow],
              layout: [...d.layout, newLayoutItem],
              // Add to all stored breakpoint layouts
              layouts: d.layouts
                ? (Object.fromEntries(
                    Object.entries(d.layouts).map(([bp, bpLayout]) => [
                      bp,
                      [...(bpLayout as Layout[]), newLayoutItem],
                    ])
                  ) as Layouts)
                : undefined,
            }
          : d
      )
    );
  }, [dashboards, currentDashboardId]);

  const removeWindow = useCallback((windowId: string) => {
    setDashboards((prev) =>
      prev.map((d) =>
        d.id === currentDashboardId
          ? {
              ...d,
              windows: d.windows.filter((w) => w.id !== windowId),
              layout: d.layout.filter((l) => l.i !== windowId),
              layouts: d.layouts
                ? (Object.fromEntries(
                    Object.entries(d.layouts).map(([bp, bpLayout]) => [
                      bp,
                      (bpLayout as Layout[]).filter((l) => l.i !== windowId),
                    ])
                  ) as Layouts)
                : undefined,
            }
          : d
      )
    );
  }, [currentDashboardId]);

  const updateWindowConfig = useCallback((windowId: string, config: Record<string, unknown>) => {
    setDashboards((prev) =>
      prev.map((d) =>
        d.id === currentDashboardId
          ? {
              ...d,
              windows: d.windows.map((w) =>
                w.id === windowId ? { ...w, config: { ...w.config, ...config } } : w
              ),
            }
          : d
      )
    );
  }, [currentDashboardId]);

  const syncTicker = useCallback((ticker: string, windowIds: string[]) => {
    setDashboards((prev) =>
      prev.map((d) =>
        d.id === currentDashboardId
          ? {
              ...d,
              windows: d.windows.map((w) =>
                windowIds.includes(w.id) ? { ...w, ticker } : w
              ),
            }
          : d
      )
    );
  }, [currentDashboardId]);

  const removeAllWindows = useCallback((dashboardId?: number) => {
    const targetId = dashboardId || currentDashboardId;
    setDashboards((prev) =>
      prev.map((d) => (d.id === targetId ? { ...d, layout: [], layouts: undefined, windows: [] } : d))
    );
  }, [currentDashboardId]);

  // Layout management
  const updateLayout = useCallback((layout: Layout[]) => {
    setDashboards((prev) =>
      prev.map((d) => (d.id === currentDashboardId ? { ...d, layout } : d))
    );
  }, [currentDashboardId]);

  const updateLayouts = useCallback((layouts: Layouts) => {
    setDashboards((prev) => {
      const current = prev.find((d) => d.id === currentDashboardId);
      if (!current) return prev;
      // RGL fires onLayoutChange on mount and whenever its internals churn.
      // A no-op still cloned every dashboard and re-rendered every widget.
      if (JSON.stringify(current.layouts) === JSON.stringify(layouts)) {
        return prev;
      }
      return prev.map((d) =>
        d.id === currentDashboardId
          ? { ...d, layouts, layout: (layouts.lg as Layout[]) || d.layout }
          : d
      );
    });
  }, [currentDashboardId]);

  const value: DashboardContextType = {
    dashboards,
    openDashboards,
    closedDashboards,
    currentDashboard,
    currentDashboardId,
    isLoading,
    isSaving,
    templates,
    switchDashboard,
    openDashboard,
    closeDashboard,
    createDashboard,
    renameDashboard,
    setDefaultDashboard,
    deleteDashboard,
    resetDashboard,
    loadTemplate,
    addWindow,
    removeWindow,
    updateWindowConfig,
    syncTicker,
    removeAllWindows,
    updateLayout,
    updateLayouts,
    saveToBackend,
    loadFromBackend,
  };

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
};

// Custom hook to use dashboard context
export const useDashboard = (): DashboardContextType => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};
